import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import SvgFragment from './SvgFragment';
import { styles } from '../styles/themes/style';
import styled from 'styled-components';

type EmojiPickerProps = {
    onSelect: (emoji: string) => void;
    trigger?: React.ReactNode;
    className?: string;
    zIndex?: number;
    offsetLeft?: number; // horizontal shift in px when opening on top
    isOpen?: boolean; // Controlled open state
    onOpenChange?: (isOpen: boolean) => void; // Callback when open state changes
    openOnHover?: boolean; // Enable hover to open (default: false for backward compatibility)
};

export const EmojiPicker: React.FC<EmojiPickerProps> = ({onSelect, trigger, className, zIndex = 10000001, offsetLeft = 0, isOpen: controlledIsOpen, onOpenChange, openOnHover = false}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (value: boolean) => {
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(value);
        } else {
            onOpenChange?.(value);
        }
    };
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pickerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [portalStyle, setPortalStyle] = useState<{
        left?: number;
        top?: number;
        bottom?: number;
    }>({});

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedInsideContainer = containerRef.current?.contains(target);
            const clickedInsidePicker = pickerRef.current?.contains(target);
            if (!clickedInsideContainer && !clickedInsidePicker) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const computePosition = () => {
            if (!triggerRef.current) return;
            const rect = triggerRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            const measuredWidth = pickerRef.current?.offsetWidth ?? 300;
            const measuredHeight = pickerRef.current?.offsetHeight ?? 350;

            const baseLeft = rect.left - offsetLeft; // configurable left shift when opening on top
            const clampedLeft = Math.min(
                Math.max(8, baseLeft),
                Math.max(8, viewportWidth - measuredWidth - 8)
            );

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Prefer opening below when ima dovoljno mesta, u suprotnom otvori iznad.
            let top: number;
            if (spaceBelow >= measuredHeight + 8 || spaceBelow >= spaceAbove) {
                top = rect.bottom + 8;
            } else {
                top = Math.max(8, rect.top - measuredHeight - 8);
            }

            setPortalStyle({ left: clampedLeft, top });
        };

        computePosition();
        window.addEventListener('resize', computePosition);
        window.addEventListener('scroll', computePosition, true);
        return () => {
            window.removeEventListener('resize', computePosition);
            window.removeEventListener('scroll', computePosition, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, offsetLeft]);

  const handleMouseEnter = () => {
    if (!openOnHover) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (!openOnHover) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <div 
      ref={containerRef} 
      className={className} 
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
        <EmojiButton
            type="button"
            ref={triggerRef}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-label="Open emoji picker"
        >
            {trigger ?? <SvgFragment type='emoji' />}
        </EmojiButton>
        {isOpen &&
            createPortal(
            <div
                ref={pickerRef}
                onMouseEnter={() => {
                  if (!openOnHover) return;
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setIsOpen(true);
                }}
                onMouseLeave={() => {
                  if (!openOnHover) return;
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setIsOpen(false);
                }}
                style={{
                    position: 'fixed',
                    zIndex,
                    left: portalStyle.left,
                    top: portalStyle.top
                }}
            >
                <Picker
                    data={data}
                    onEmojiSelect={(e: any) => {
                        onSelect(e.native);
                        setIsOpen(false);
                    }}
                    theme="light"
                    locale="sr"
                />
            </div>,
            document.body
            )}
    </div>
  );
};

const EmojiButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  line-height: 1;

  &:hover svg path {
    fill: ${styles.colors.primary600} !important;
  }
`;